import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SmilesDrawer from 'smiles-drawer/src/SmilesDrawer';
import ContactFooter from '../components/ContactFooter';

gsap.registerPlugin(ScrollTrigger);

const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=IBM+Plex+Mono:wght@400;500&family=Syne:wght@400;600&display=swap';

const C = {
  bg:       '#ffffff',
  bgAlt:    '#f3f1fb',
  ink:      '#1b1530',
  dim:      '#6c6685',
  violet:   '#7c3aed',
  violetLo: 'rgba(124,58,237,0.55)',
  amber:    '#d97706',
  amberLo:  'rgba(217,119,6,0.55)',
  storm:    '#e11d6b',
  stormLo:  'rgba(225,29,107,0.6)',
  calm:     '#0d9488',
  line:     'rgba(27,21,48,0.10)',
};

const STEPS = [
  {
    num: '01 / 05',
    chapter: 'Baseline',
    title: 'Normal lysine catabolism.',
    body: 'In healthy cells, the enzyme antiquitin (ALDH7A1) oxidizes α-aminoadipic semialdehyde (α-AASA) as a step in lysine breakdown. Pyridoxal 5′-phosphate (PLP) — the active form of vitamin B6 — serves as a cofactor for roughly 140 enzymes, including glutamate decarboxylase (GAD), which produces the inhibitory neurotransmitter GABA.',
    badge: 'Cofactor available',
    tone: 'ok',
    plpLevel: 1.00,
    show: ['lys', 'antq', 'aaa', 'plp-pool', 'gad', 'gaba', 'flow-1', 'flow-2', 'flow-3'],
    hide: ['antq-x', 'p6c', 'trap', 'storm', 'rescue'],
  },
  {
    num: '02 / 05',
    chapter: 'Enzyme deficiency',
    title: 'Antiquitin is non-functional.',
    body: 'Biallelic loss-of-function variants in ALDH7A1 abolish antiquitin activity. The lysine catabolic pathway stalls: α-AASA can no longer be converted to α-aminoadipate, so it begins to accumulate.',
    badge: 'ALDH7A1 deficiency',
    tone: 'bad',
    plpLevel: 0.82,
    show: ['lys', 'antq-x', 'plp-pool', 'gad', 'gaba', 'flow-1'],
    hide: ['antq', 'aaa', 'p6c', 'trap', 'flow-2', 'flow-3', 'storm', 'rescue'],
  },
  {
    num: '03 / 05',
    chapter: 'PLP depletion',
    title: 'P6C reacts with PLP.',
    body: 'α-AASA is in spontaneous equilibrium with its cyclic tautomer, Δ¹-piperideine-6-carboxylate (P6C). P6C condenses with PLP via a Knoevenagel reaction, forming a stable, catalytically inactive adduct. As this reaction continues, the free PLP pool is progressively reduced.',
    badge: 'Cofactor sequestration',
    tone: 'bad',
    plpLevel: 0.32,
    show: ['lys', 'antq-x', 'p6c', 'trap', 'plp-pool', 'flow-1'],
    hide: ['antq', 'aaa', 'gad', 'gaba', 'flow-2', 'flow-3', 'storm', 'rescue'],
  },
  {
    num: '04 / 05',
    chapter: 'Seizure onset',
    title: 'GABA synthesis is impaired.',
    body: 'With insufficient PLP, glutamate decarboxylase cannot maintain normal GABA production. Reduced inhibitory neurotransmission disturbs the cortical excitation–inhibition balance. In untreated neonates this results in seizures beginning within hours to days of birth that do not respond to standard anticonvulsant medications.',
    badge: 'Neonatal seizure',
    tone: 'bad',
    plpLevel: 0.05,
    show: ['lys', 'antq-x', 'p6c', 'trap', 'plp-pool', 'storm'],
    hide: ['antq', 'aaa', 'gad', 'gaba', 'flow-1', 'flow-2', 'flow-3', 'rescue'],
  },
  {
    num: '05 / 05',
    chapter: 'Treatment',
    title: 'Restoring the PLP pool.',
    body: '· Pharmacologic pyridoxine (15–30 mg/kg/day) generates PLP at a rate that exceeds P6C sequestration, gradually rebuilding the free cofactor pool\n· Lysine-restricted diet reduces α-AASA production at the metabolic source\n· Arginine supplementation competes with lysine for transport into cells and across the blood–brain barrier\n· This triple-therapy approach is the current standard of care; cognitive outcomes remain variable',
    badge: 'B6-responsive',
    tone: 'ok',
    plpLevel: 0.90,
    show: ['lys', 'antq-x', 'p6c', 'plp-pool', 'gad', 'gaba', 'rescue', 'flow-1', 'flow-2', 'flow-3'],
    hide: ['antq', 'aaa', 'trap', 'storm'],
  },
];

const MOLS = {
  lysine: {
    name: 'L-Lysine',
    smiles: 'N[C@@H](CCCCN)C(=O)O',
    role: 'Substrate',
    desc: 'Essential amino acid and the starting material for the saccharopine pathway. Reducing dietary lysine decreases α-AASA and P6C production — one component of triple therapy.',
  },
  aasa: {
    name: 'α-AASA',
    smiles: 'O=CCCC[C@H](N)C(=O)O',
    role: 'Accumulates',
    desc: 'α-Aminoadipic semialdehyde. Accumulates when antiquitin is absent. Elevated levels in urine and plasma are used to confirm a PDE diagnosis.',
  },
  p6c: {
    name: 'P6C',
    smiles: 'O=C(O)C1CCCC=N1',
    role: 'PLP trap',
    desc: 'Δ¹-Piperideine-6-carboxylate — the cyclic tautomer of α-AASA. Its imine group reacts with PLP\'s aldehyde to form a stable, inactive adduct, removing the cofactor from circulation.',
  },
  plp: {
    name: 'PLP',
    smiles: 'Cc1ncc(COP(=O)(O)O)c(C=O)c1O',
    role: 'Cofactor',
    desc: 'Pyridoxal 5′-phosphate — the active form of vitamin B6. Required by roughly 140 human enzymes. Its aldehyde group is both essential for catalysis and the site targeted by P6C.',
  },
  pyridoxine: {
    name: 'Pyridoxine',
    smiles: 'Cc1ncc(CO)c(CO)c1O',
    role: 'Therapy',
    desc: 'The B6 vitamer used therapeutically. Phosphorylated to PLP in vivo; at pharmacologic doses the rate of PLP production exceeds the rate of P6C-mediated sequestration.',
  },
  gaba: {
    name: 'GABA',
    smiles: 'NCCCC(=O)O',
    role: 'Neurotransmitter',
    desc: 'γ-Aminobutyric acid — the primary inhibitory neurotransmitter. Produced by glutamate decarboxylase (GAD), which requires PLP as a cofactor. PLP depletion reduces GABA output.',
  },
};

const TIMELINE = [
  { t: 'Hour 0',      label: 'Birth',                note: 'Pregnancy and delivery are typically unremarkable. There is usually no prenatal clinical indication of the diagnosis.' },
  { t: 'Hours–days',  label: 'First seizures',       note: 'Seizures begin in the neonatal period — sometimes within the first few hours of life, sometimes over the first several days.' },
  { t: 'Early',       label: 'Anticonvulsants fail', note: 'Standard first-line medications such as phenobarbital do not control the seizures, which is itself a meaningful diagnostic clue.' },
  { t: 'Diagnostic',  label: 'Pyridoxine trial',     note: 'Pyridoxine is administered under monitoring as a deliberate diagnostic test for B6-responsiveness.' },
  { t: 'Minutes',     label: 'Seizures resolve',     note: 'A clear, rapid response to B6 is the hallmark feature that points clinicians toward a PDE diagnosis.' },
  { t: 'Lifelong',    label: 'Maintenance therapy',  note: 'Ongoing pyridoxine, lysine restriction, and arginine supplementation continue throughout the patient\'s life.' },
];

const THERAPY_ARMS = [
  {
    key: 'pyridoxine',
    name: 'High-dose pyridoxine',
    tag: 'replenishes PLP supply',
    desc: 'Pharmacologic doses of B6 are converted to PLP faster than P6C can sequester it, gradually restoring the free cofactor pool available to PLP-dependent enzymes.',
    plp: 0.60,
    trap: 1,
  },
  {
    key: 'lysine',
    name: 'Lysine restriction',
    tag: 'reduces P6C production',
    desc: 'Lower dietary lysine reduces flux through the saccharopine pathway, decreasing α-AASA and therefore the P6C available to react with PLP.',
    plp: 0.16,
    trap: 0.42,
  },
  {
    key: 'arginine',
    name: 'Arginine supplementation',
    tag: 'competes for lysine transport',
    desc: 'Arginine competes with lysine at shared amino acid transporters in the gut, liver, and blood–brain barrier, reducing cellular lysine uptake and P6C formation.',
    plp: 0.10,
    trap: 0.72,
  },
];

const SOURCES = [
  { cite: 'Mills PB, Struys E, Jakobs C, et al. Mutations in antiquitin in individuals with pyridoxine-dependent seizures. Nat Med. 2006;12(3):307–309.', href: 'https://doi.org/10.1038/nm1366' },
  { cite: 'Coughlin CR 2nd, Tseng LA, Bok LA, et al. Consensus guidelines for the diagnosis and management of pyridoxine-dependent epilepsy due to α-aminoadipic semialdehyde dehydrogenase deficiency. J Inherit Metab Dis. 2021;44(1):178–192.', href: 'https://doi.org/10.1002/jimd.12332' },
  { cite: 'Dixon M, Millington C, Bernstein L, et al. Dietary management for pyridoxine-dependent epilepsy due to α-aminoadipic semialdehyde dehydrogenase deficiency, a follow-on from the international consortium guidelines. JIMD Rep. 2024;65(3):188–203.', href: 'https://doi.org/10.1002/jmd2.12418' },
  { cite: 'Gospe SM Jr. Pyridoxine-Dependent Epilepsy – ALDH7A1. GeneReviews. University of Washington, Seattle; updated 2022.', href: 'https://www.ncbi.nlm.nih.gov/books/NBK1486/' },
  { cite: 'Online Mendelian Inheritance in Man (OMIM). #266100 — Pyridoxine-dependent epilepsy.', href: 'https://omim.org/entry/266100' },
  { cite: 'Struys EA, Jakobs C. Metabolism of lysine in α-aminoadipic semialdehyde dehydrogenase-deficient fibroblasts. FEBS Lett. 2010;584(1):181–186.', href: 'https://doi.org/10.1016/j.febslet.2009.11.055' },
  { cite: 'Hallen A, Jamie JF, Cooper AJL. Lysine metabolism in mammalian brain: an update on the importance of recent discoveries. Amino Acids. 2013;45(6):1249–1272.', href: 'https://doi.org/10.1007/s00726-013-1590-1' },
];

const HELP_WAYS = [
  { t: 'Give', d: 'Support natural-history studies and gene-therapy trials that depend on patient registries and sustained research funding.' },
  { t: 'Connect families', d: 'Help newly diagnosed families find the PDE community and the registries that make clinical trials possible.' },
  { t: 'Raise awareness', d: 'Earlier recognition of B6-responsive seizures leads to earlier treatment, which can improve developmental outcomes.' },
];

const HELP_FOUNDATIONS = [
  { name: 'National Organization for Rare Disorders (NORD)', desc: 'Patient assistance and advocacy programs across rare conditions.', url: 'https://rarediseases.org' },
  { name: 'Epilepsy Foundation', desc: 'Helpline, family support, and advocacy for epilepsy care.', url: 'https://www.epilepsy.com' },
  { name: 'CURE Epilepsy', desc: 'Funds scientific research toward epilepsy cures.', url: 'https://www.cureepilepsy.org' },
  { name: 'Child Neurology Foundation', desc: 'Supports families navigating childhood neurologic conditions.', url: 'https://www.childneurologyfoundation.org' },
];

const FACTS = [
  { k: 'Gene',       v: 'ALDH7A1' },
  { k: 'OMIM',       v: '266100' },
  { k: 'Enzyme',     v: 'Antiquitin' },
  { k: 'Prevalence', v: '~1:400,000' },
  { k: 'Onset',      v: 'Neonatal' },
  { k: 'Biomarker',  v: 'α-AASA' },
];

const mono = { fontFamily: "'IBM Plex Mono', monospace" };
const serif = { fontFamily: "'Instrument Serif', serif" };

const S = {
  root: { background: C.bg, color: C.ink, fontFamily: "'Syne', sans-serif", overflowX: 'hidden' },
  sectionLabel: { ...mono, fontSize: '0.6rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: C.violet, marginBottom: '0.8rem' },
  sectionHeading: { ...serif, fontSize: 'clamp(1.7rem, 4vw, 3.4rem)', lineHeight: 1.08 },
  sectionBlurb: { ...mono, fontSize: '0.78rem', lineHeight: 1.85, color: C.dim },
  card: { border: `1px solid ${C.line}`, borderRadius: '12px', background: C.bg },
};

function sec(mobile) {
  return { padding: mobile ? '4rem 5vw' : '8rem 6vw' };
}

function badgeStyle(tone) {
  const ok = tone === 'ok';
  return {
    display: 'inline-block', marginTop: '1.5rem',
    ...mono, fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase',
    padding: '0.35rem 0.85rem', borderRadius: '999px',
    background: ok ? 'rgba(13,148,136,0.12)' : 'rgba(225,29,107,0.07)',
    color: ok ? C.calm : C.storm,
    border: ok ? '1px solid rgba(13,148,136,0.4)' : '1px solid rgba(225,29,107,0.35)',
  };
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);
  return isMobile;
}

function PathwaySVG({ vis, plpLevel = 1 }) {
  const o = (id) => vis[id] ?? 0;
  const level = Math.max(0, Math.min(1, plpLevel));
  return (
    <svg
      viewBox="0 0 520 360"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', maxWidth: '520px', height: 'auto', overflow: 'visible', display: 'block' }}
    >
      <defs>
        <marker id="p3-ma-v" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill={C.violetLo} />
        </marker>
        <marker id="p3-ma-s" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill={C.stormLo} />
        </marker>
        <filter id="p3-glow-v">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="p3-glow-s">
          <feGaussianBlur stdDeviation="6" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="p3-pool-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.amber} stopOpacity="0.35" />
          <stop offset="100%" stopColor={C.amber} stopOpacity="0.05" />
        </linearGradient>
        <pattern id="p3-dots" width="6" height="6" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.6" fill="rgba(27,21,48,0.16)" />
        </pattern>
      </defs>

      <line x1="160" y1="40" x2="160" y2="320" stroke="rgba(27,21,48,0.10)" strokeWidth="1" strokeDasharray="2 4" />

      <g opacity={o('lys')} style={{ transition: 'opacity 0.5s' }}>
        <circle cx="160" cy="55" r="32" fill={C.bg} stroke={C.violetLo} strokeWidth="1.5" />
        <text x="160" y="52" textAnchor="middle" fontFamily="Syne" fontSize="11" fill={C.ink}>Lysine</text>
        <text x="160" y="66" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7.5" fill={C.dim}>C₆H₁₄N₂O₂</text>
      </g>

      <line opacity={o('flow-1')} x1="160" y1="90" x2="160" y2="118" stroke={C.violetLo} strokeWidth="1.5" markerEnd="url(#p3-ma-v)" style={{ transition: 'opacity 0.5s' }} />

      <g opacity={o('antq')} style={{ transition: 'opacity 0.5s' }}>
        <rect x="100" y="120" width="120" height="38" rx="3" fill="rgba(124,58,237,0.08)" stroke={C.violetLo} strokeWidth="1.2" />
        <text x="160" y="137" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9" fill={C.violet}>ANTIQUITIN</text>
        <text x="160" y="151" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7.5" fill={C.dim}>ALDH7A1 · active</text>
      </g>

      <g opacity={o('antq-x')} style={{ transition: 'opacity 0.5s' }}>
        <rect x="100" y="120" width="120" height="38" rx="3" fill="rgba(225,29,107,0.07)" stroke={C.stormLo} strokeWidth="1.4" strokeDasharray="3 3" />
        <text x="160" y="137" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9" fill={C.storm}>ANTIQUITIN ✕</text>
        <text x="160" y="151" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7.5" fill={C.stormLo}>biallelic loss</text>
        <line x1="118" y1="125" x2="202" y2="153" stroke={C.stormLo} strokeWidth="1" />
      </g>

      <line opacity={o('flow-2')} x1="160" y1="160" x2="160" y2="188" stroke={C.violetLo} strokeWidth="1.5" markerEnd="url(#p3-ma-v)" style={{ transition: 'opacity 0.5s' }} />

      <g opacity={o('aaa')} style={{ transition: 'opacity 0.5s' }}>
        <circle cx="160" cy="215" r="28" fill={C.bg} stroke={C.violetLo} strokeWidth="1.2" />
        <text x="160" y="213" textAnchor="middle" fontFamily="Syne" fontSize="10" fill={C.ink}>α-AAA</text>
        <text x="160" y="225" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.dim}>cleared</text>
      </g>

      <g opacity={o('p6c')} style={{ transition: 'opacity 0.5s' }} filter="url(#p3-glow-v)">
        <circle cx="160" cy="215" r="34" fill="rgba(225,29,107,0.07)" stroke={C.storm} strokeWidth="1.4" />
        <text x="160" y="212" textAnchor="middle" fontFamily="Syne" fontSize="11" fill={C.storm}>P6C</text>
        <text x="160" y="225" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.stormLo}>↑ accumulating</text>
        <circle cx="160" cy="215" r="48" fill="none" stroke="rgba(225,29,107,0.20)" strokeWidth="1" strokeDasharray="3 4" />
      </g>

      <g opacity={o('plp-pool')} style={{ transition: 'opacity 0.5s' }}>
        <rect x="360" y="80" width="110" height="140" rx="2" fill="none" stroke={C.amberLo} strokeWidth="1.2" />
        <g style={{ transform: `scaleY(${level})`, transformOrigin: '365px 220px', transition: 'transform 1.1s cubic-bezier(.5,0,.2,1)' }}>
          <rect x="360" y="80" width="110" height="140" rx="2" fill="url(#p3-pool-grad)" />
          <rect x="360" y="80" width="110" height="140" rx="2" fill="url(#p3-dots)" />
          <line x1="360" y1="80" x2="470" y2="80" stroke={C.amber} strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </g>
        {level < 0.95 && (
          <g opacity={1 - level}>
            {[0, 1, 2].map((i) => (
              <circle key={i} cx={378 + i * 18} cy={Math.max(82, 220 - 140 * level - 12)} r="1.6" fill={C.amber}>
                <animate attributeName="cy" values={`${Math.max(82, 220 - 140 * level - 14)};${220 - 140 * level + 4};${Math.max(82, 220 - 140 * level - 14)}`} dur={`${1.4 + i * 0.2}s`} repeatCount="indefinite" begin={`${i * 0.25}s`} />
                <animate attributeName="opacity" values="0.9;0;0.9" dur={`${1.4 + i * 0.2}s`} repeatCount="indefinite" begin={`${i * 0.25}s`} />
              </circle>
            ))}
          </g>
        )}
        <text x="415" y="68" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" letterSpacing="0.15em" fill={C.amber}>PLP POOL</text>
        <text x="415" y="240" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.dim}>active B6 · cofactor</text>
        <text x="478" y="92" textAnchor="start" fontFamily="IBM Plex Mono" fontSize="10" fontWeight="500" fill={level < 0.3 ? C.storm : C.amber} style={{ transition: 'fill 0.5s' }}>
          {Math.round(level * 100)}%
        </text>
        <text x="478" y="103" textAnchor="start" fontFamily="IBM Plex Mono" fontSize="6.5" fill={C.dim} letterSpacing="0.12em">FREE</text>
        <line x1="358" y1="100" x2="354" y2="100" stroke={C.amberLo} strokeWidth="1" />
        <line x1="358" y1="140" x2="354" y2="140" stroke={C.amberLo} strokeWidth="1" />
        <line x1="358" y1="180" x2="354" y2="180" stroke={C.amberLo} strokeWidth="1" />
      </g>

      <g opacity={o('trap')} style={{ transition: 'opacity 0.5s' }}>
        <path d="M 360 150 C 320 150, 260 200, 200 215" stroke={C.storm} strokeWidth="1.6" strokeDasharray="4 3" fill="none" markerEnd="url(#p3-ma-s)" />
        <text x="290" y="178" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7.5" fill={C.storm} letterSpacing="0.12em">PLP SEQUESTERED</text>
        <circle cx="340" cy="170" r="2" fill={C.amber} opacity="0.7">
          <animate attributeName="cy" values="160;200;160" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.8;0;0.8" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="305" cy="190" r="2" fill={C.amber} opacity="0.6">
          <animate attributeName="cy" values="180;215;180" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;0;0.7" dur="2.4s" repeatCount="indefinite" />
        </circle>
      </g>

      <g opacity={o('rescue')} style={{ transition: 'opacity 0.5s' }}>
        <text x="415" y="50" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" fill={C.amber} letterSpacing="0.15em">+ B6 DOSE</text>
        {[0, 1, 2, 3, 4].map((i) => (
          <circle key={i} cx={375 + i * 20} cy="60" r="2" fill={C.amber}>
            <animate attributeName="cy" values="55;82;55" dur={`${1.2 + i * 0.15}s`} repeatCount="indefinite" begin={`${i * 0.18}s`} />
            <animate attributeName="opacity" values="1;0.2;1" dur={`${1.2 + i * 0.15}s`} repeatCount="indefinite" begin={`${i * 0.18}s`} />
          </circle>
        ))}
      </g>

      <g opacity={o('gad')} style={{ transition: 'opacity 0.5s' }}>
        <line x1="415" y1="220" x2="415" y2="258" stroke={C.violetLo} strokeWidth="1.2" markerEnd="url(#p3-ma-v)" />
        <rect x="378" y="260" width="74" height="28" rx="2" fill="rgba(13,148,136,0.09)" stroke="rgba(13,148,136,0.5)" strokeWidth="1" />
        <text x="415" y="278" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" fill={C.calm}>GAD · GABA</text>
      </g>

      <line opacity={o('flow-3')} x1="415" y1="290" x2="415" y2="310" stroke="rgba(13,148,136,0.5)" strokeWidth="1.2" style={{ transition: 'opacity 0.5s' }} />

      <g opacity={o('gaba')} style={{ transition: 'opacity 0.5s' }}>
        <text x="415" y="325" textAnchor="middle" fontFamily="Syne" fontSize="11" fill={C.calm}>inhibition</text>
        <text x="415" y="338" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.dim}>cortical balance</text>
      </g>

      <g opacity={o('storm')} style={{ transition: 'opacity 0.5s' }} filter="url(#p3-glow-s)">
        <circle cx="415" cy="290" r="42" fill="rgba(225,29,107,0.07)" stroke={C.storm} strokeWidth="1.5" />
        {[...Array(8)].map((_, i) => {
          const a = (i / 8) * Math.PI * 2;
          return <line key={i} x1={415 + Math.cos(a) * 30} y1={290 + Math.sin(a) * 30} x2={415 + Math.cos(a) * 50} y2={290 + Math.sin(a) * 50} stroke={C.storm} strokeWidth="1.4" opacity="0.7" />;
        })}
        <text x="415" y="288" textAnchor="middle" fontFamily="Syne" fontSize="10" fill={C.storm}>seizure</text>
        <text x="415" y="300" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.stormLo}>refractory</text>
      </g>
    </svg>
  );
}

function CascadeSVG() {
  const nodes = [
    { id: 'lys',   x: 60,   label: 'Lysine',         sub: 'C₆H₁₄N₂O₂',          tone: 'ink' },
    { id: 'sac',   x: 220,  label: 'Saccharopine',   sub: 'intermediate',         tone: 'ink' },
    { id: 'aasa',  x: 400,  label: 'α-AASA',         sub: 'open-chain aldehyde',  tone: 'storm' },
    { id: 'p6c',   x: 540,  label: 'P6C',            sub: 'cyclic imine',         tone: 'storm' },
    { id: 'aaa',   x: 720,  label: 'α-aminoadipate', sub: 'cleared form',         tone: 'dim' },
    { id: 'aka',   x: 890,  label: 'α-ketoadipate',  sub: '',                     tone: 'dim' },
    { id: 'glcoa', x: 1050, label: 'glutaryl-CoA',   sub: '… → acetyl-CoA',       tone: 'dim' },
  ];
  const toneColor = (t) => t === 'storm' ? C.storm : t === 'dim' ? C.dim : C.ink;
  const enzymes = [
    { from: 0, to: 1, name: 'LKR',        sub: 'AASS · domain 1' },
    { from: 1, to: 2, name: 'SDH',        sub: 'AASS · domain 2' },
    { from: 3, to: 4, name: 'ANTIQUITIN', sub: 'ALDH7A1 ✕ blocked', bad: true },
    { from: 4, to: 5, name: 'AADAT',      sub: '' },
    { from: 5, to: 6, name: 'DHTKD1',     sub: '' },
  ];
  const W = 1140; const H = 240; const yMid = 130;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', minWidth: 980, height: 'auto', overflow: 'visible' }}>
      <defs>
        <marker id="p3-cm-v" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0 L0,6 L7,3z" fill={C.violetLo} /></marker>
        <marker id="p3-cm-d" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0 L0,6 L7,3z" fill="rgba(106,100,126,0.7)" /></marker>
        <marker id="p3-cm-s" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0 L0,6 L7,3z" fill={C.stormLo} /></marker>
        <marker id="p3-cm-a" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0 L0,6 L7,3z" fill={C.amberLo} /></marker>
      </defs>
      {enzymes.map((e, i) => {
        const a = nodes[e.from]; const b = nodes[e.to];
        const x1 = a.x + 38; const x2 = b.x - 38;
        const stroke = e.bad ? C.storm : C.violetLo;
        const marker = e.bad ? 'url(#p3-cm-s)' : 'url(#p3-cm-v)';
        return (
          <g key={i}>
            <line x1={x1} y1={yMid} x2={x2} y2={yMid} stroke={stroke} strokeWidth={e.bad ? 1.6 : 1.4} strokeDasharray={e.bad ? '5 4' : undefined} markerEnd={marker} />
            <text x={(x1 + x2) / 2} y={yMid - 14} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8.5" letterSpacing="0.12em" fill={e.bad ? C.storm : C.violet}>{e.name}</text>
            {e.sub && <text x={(x1 + x2) / 2} y={yMid + 22} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={e.bad ? C.stormLo : C.dim}>{e.sub}</text>}
            {e.bad && (
              <g>
                <line x1={(x1 + x2) / 2 - 6} y1={yMid - 6} x2={(x1 + x2) / 2 + 6} y2={yMid + 6} stroke={C.storm} strokeWidth="1.6" />
                <line x1={(x1 + x2) / 2 - 6} y1={yMid + 6} x2={(x1 + x2) / 2 + 6} y2={yMid - 6} stroke={C.storm} strokeWidth="1.6" />
              </g>
            )}
          </g>
        );
      })}
      <g>
        <path d={`M ${nodes[2].x + 36} ${yMid - 6} Q ${(nodes[2].x + nodes[3].x) / 2} ${yMid - 28} ${nodes[3].x - 36} ${yMid - 6}`} stroke={C.stormLo} strokeWidth="1.3" fill="none" markerEnd="url(#p3-cm-s)" />
        <path d={`M ${nodes[3].x - 36} ${yMid + 6} Q ${(nodes[2].x + nodes[3].x) / 2} ${yMid + 28} ${nodes[2].x + 36} ${yMid + 6}`} stroke={C.stormLo} strokeWidth="1.3" fill="none" markerEnd="url(#p3-cm-s)" />
        <text x={(nodes[2].x + nodes[3].x) / 2} y={yMid - 36} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" letterSpacing="0.12em" fill={C.storm}>spontaneous</text>
        <text x={(nodes[2].x + nodes[3].x) / 2} y={yMid + 48} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.stormLo}>equilibrium · pH-driven</text>
      </g>
      <g>
        <path d={`M ${nodes[3].x} ${yMid + 36} Q ${nodes[3].x + 30} ${yMid + 80} ${nodes[3].x + 80} ${yMid + 90}`} stroke={C.amberLo} strokeWidth="1.4" strokeDasharray="4 3" fill="none" markerEnd="url(#p3-cm-a)" />
        <text x={nodes[3].x + 6} y={yMid + 70} fontFamily="IBM Plex Mono" fontSize="7.5" fill={C.amber} letterSpacing="0.1em">+ PLP</text>
        <g transform={`translate(${nodes[3].x + 80}, ${yMid + 92})`}>
          <rect x="-44" y="-12" width="88" height="26" rx="2" fill="rgba(217,119,6,0.10)" stroke={C.amberLo} strokeWidth="1" strokeDasharray="3 3" />
          <text x="0" y="-1" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" fill={C.amber}>P6C–PLP adduct</text>
          <text x="0" y="10" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.dim}>inactive</text>
        </g>
      </g>
      <g>
        <path d={`M ${nodes[0].x + 20} ${yMid + 60} Q ${nodes[1].x} ${yMid + 110} ${nodes[3].x - 36} ${yMid + 18}`} stroke="rgba(106,100,126,0.5)" strokeWidth="1" strokeDasharray="2 4" fill="none" markerEnd="url(#p3-cm-d)" />
        <text x={(nodes[0].x + nodes[3].x) / 2 - 30} y={yMid + 118} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.dim} letterSpacing="0.1em">minor · pipecolate route (brain)</text>
      </g>
      {nodes.map((n) => {
        const isAcc = n.tone === 'storm';
        return (
          <g key={n.id}>
            {isAcc && <circle cx={n.x} cy={yMid} r="44" fill="none" stroke="rgba(225,29,107,0.22)" strokeWidth="1" strokeDasharray="3 4" />}
            <circle cx={n.x} cy={yMid} r="34" fill={isAcc ? 'rgba(225,29,107,0.07)' : C.bg} stroke={isAcc ? C.storm : n.tone === 'ink' ? C.violetLo : 'rgba(106,100,126,0.5)'} strokeWidth="1.3" />
            <text x={n.x} y={yMid - 1} textAnchor="middle" fontFamily="Syne" fontSize="10" fill={toneColor(n.tone === 'dim' ? 'dim' : 'ink')}>{n.label}</text>
            {n.sub && <text x={n.x} y={yMid + 12} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="6.5" fill={C.dim}>{n.sub}</text>}
            {isAcc && <text x={n.x} y={yMid - 50} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.storm} letterSpacing="0.12em">↑ ACCUMULATES</text>}
          </g>
        );
      })}
      <text x="20" y="24" fontFamily="IBM Plex Mono" fontSize="8" letterSpacing="0.2em" fill={C.dim}>SACCHAROPINE PATHWAY · MITOCHONDRION → CYTOSOL</text>
    </svg>
  );
}

function DependencyWeb({ isMobile }) {
  const [plp, setPlp] = useState(100);
  const [thresholds] = useState(() => Array.from({ length: 139 }, (_, i) => 6 + ((i * 61) % 88)));
  const gadThreshold = 56;
  const gadOk = plp >= gadThreshold;
  const totalSupplied = thresholds.filter((t) => t <= plp).length + (gadOk ? 1 : 0);

  let caption;
  if (plp >= 85)           caption = 'All ~140 PLP-dependent enzymes have sufficient cofactor. GAD is producing GABA, and cortical inhibitory tone is maintained.';
  else if (plp >= gadThreshold) caption = 'Free PLP is declining. Enzymes with high cofactor requirements drop out first — GAD is still active but approaching its threshold.';
  else if (plp >= 22)      caption = 'PLP is below the level needed for GAD. GABA production decreases, and the excitation–inhibition balance is disturbed.';
  else                     caption = 'Free PLP is severely depleted. Activity across the ~140 dependent enzymes is broadly reduced, with GABA synthesis among the most clinically significant losses.';

  return (
    <section style={{ ...sec(isMobile), borderTop: `1px solid ${C.line}`, background: C.bg }}>
      <div style={S.sectionLabel}>PLP-dependent metabolism</div>
      <div style={{ ...S.sectionHeading, maxWidth: '640px', marginBottom: '1.2rem' }}>
        One cofactor required by ~140 enzymes.
      </div>
      <p style={{ ...S.sectionBlurb, maxWidth: '560px', marginBottom: '2.5rem' }}>
        PLP is the working cofactor for roughly 140 human enzymes involved in amino acid metabolism, neurotransmitter synthesis, and other processes. When P6C draws it into an inactive adduct, the effects extend well beyond a single pathway. Use the slider to adjust the free-PLP level.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1.5rem', marginBottom: '2.5rem' }}>
        <input
          className="pde3-range"
          type="range" min="0" max="100" value={plp}
          onChange={(e) => setPlp(Number(e.target.value))}
          style={{ flex: '1 1 240px', maxWidth: '420px' }}
        />
        <div style={{ ...mono, fontSize: '0.7rem', letterSpacing: '0.12em', color: C.dim, textTransform: 'uppercase' }}>
          Free PLP <span style={{ color: C.violet, fontSize: '1.1rem' }}>{plp}%</span>
          {' · '}
          <span style={{ color: C.ink }}>{totalSupplied}</span> / 140 enzymes supplied
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(200px, 260px) 1fr', gap: '1.2rem', alignItems: 'stretch' }}>
        <div style={{
          ...S.card,
          borderColor: gadOk ? 'rgba(13,148,136,0.45)' : C.stormLo,
          background: gadOk ? 'rgba(13,148,136,0.06)' : 'rgba(225,29,107,0.05)',
          padding: '1.6rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', transition: 'all 0.4s',
        }}>
          <div style={{ ...mono, fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: gadOk ? C.calm : C.storm }}>
            Clinically critical enzyme
          </div>
          <div style={{ ...serif, fontSize: '2rem', lineHeight: 1 }}>GAD</div>
          <div style={{ ...mono, fontSize: '0.7rem', color: C.dim, lineHeight: 1.7 }}>
            Glutamate decarboxylase. Converts glutamate to GABA, the primary inhibitory neurotransmitter.
          </div>
          <div style={{ marginTop: 'auto', paddingTop: '1rem', ...mono, fontSize: '0.72rem', fontWeight: 500, color: gadOk ? C.calm : C.storm }}>
            {gadOk ? '● Active — GABA synthesis continuing' : '○ Insufficient PLP — GABA synthesis reduced'}
          </div>
        </div>

        <div style={{ ...S.card, padding: '1.6rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ ...mono, fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.dim }}>
            + 139 other PLP-dependent enzymes
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {thresholds.map((t, i) => {
              const ok = t <= plp;
              return (
                <span key={i} style={{
                  width: 12, height: 12, borderRadius: 3,
                  background: ok ? C.violet : 'transparent',
                  border: ok ? 'none' : `1px solid rgba(27,21,48,0.16)`,
                  opacity: ok ? 1 : 0.55,
                  transition: 'background 0.3s, opacity 0.3s',
                }} />
              );
            })}
          </div>
          <div style={{ ...mono, fontSize: '0.65rem', color: C.dim }}>
            Each square represents one enzyme — transaminases, synthases, decarboxylases — that requires PLP to function.
          </div>
        </div>
      </div>

      <p style={{ ...mono, fontSize: '0.82rem', lineHeight: 1.8, color: plp >= gadThreshold ? C.ink : C.storm, maxWidth: '680px', marginTop: '2.5rem', transition: 'color 0.4s' }}>
        {caption}
      </p>

      <p style={{ ...mono, fontSize: '0.62rem', lineHeight: 1.7, color: C.dim, maxWidth: '680px', marginTop: '1.4rem', fontStyle: 'italic' }}>
        Illustrative model. PLP is genuinely required by roughly 140 human enzymes, and impaired GABA synthesis via GAD is the clinically significant consequence of depletion. The individual thresholds shown here are schematic — there is no established quantitative ordering of the exact free-PLP level at which each enzyme loses activity.
      </p>
    </section>
  );
}

function TripleTherapy({ isMobile }) {
  const [on, setOn] = useState({ pyridoxine: false, lysine: false, arginine: false });
  const toggle = (k) => setOn((s) => ({ ...s, [k]: !s[k] }));
  const plpPool = Math.min(0.98, 0.10 + THERAPY_ARMS.reduce((sum, a) => sum + (on[a.key] ? a.plp : 0), 0));
  const trap = THERAPY_ARMS.reduce((p, a) => p * (on[a.key] ? a.trap : 1), 1);
  const count = Object.values(on).filter(Boolean).length;

  let caption;
  if (count === 0)                   caption = 'Without treatment, the P6C–PLP reaction runs continuously. Free cofactor remains low and seizures do not respond to standard anticonvulsants.';
  else if (on.pyridoxine && count === 1) caption = 'Pyridoxine alone typically controls seizures, though the underlying P6C sequestration continues and the free PLP pool remains partially depleted.';
  else if (count === 3)              caption = "Triple therapy is the current standard of care. The three components address the problem through complementary mechanisms. Seizures typically resolve; cognitive outcomes remain variable.";
  else                               caption = 'Each component acts through a distinct mechanism. Combined use is more effective than any single approach alone.';

  const Bar = ({ label, value, color, sub }) => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', ...mono, fontSize: '0.62rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: '0.5rem' }}>
        <span>{label}</span><span style={{ color }}>{Math.round(value * 100)}%</span>
      </div>
      <div style={{ height: 12, borderRadius: 999, background: 'rgba(27,21,48,0.05)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${value * 100}%`, background: color, borderRadius: 999, transition: 'width 0.6s cubic-bezier(.5,0,.2,1)' }} />
      </div>
      <div style={{ ...mono, fontSize: '0.6rem', color: C.dim, marginTop: '0.4rem' }}>{sub}</div>
    </div>
  );

  return (
    <section style={{ ...sec(isMobile), borderTop: `1px solid ${C.line}`, background: C.bgAlt }}>
      <div style={{ ...S.sectionLabel, color: C.amber }}>Interactive · treatment</div>
      <div style={{ ...S.sectionHeading, marginBottom: '1.2rem' }}>Three-component treatment strategy.</div>
      <p style={{ ...S.sectionBlurb, maxWidth: '560px', marginBottom: '2.5rem' }}>
        The P6C–PLP reaction does not stop with treatment — each therapy component addresses it through a different mechanism. Toggle the arms to observe their individual and combined effects.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        {THERAPY_ARMS.map((a) => {
          const active = on[a.key];
          return (
            <button key={a.key} onClick={() => toggle(a.key)} style={{
              ...S.card, textAlign: 'left', cursor: 'pointer',
              borderColor: active ? C.amber : C.line,
              background: active ? 'rgba(217,119,6,0.07)' : C.bg,
              padding: '1.4rem', display: 'flex', flexDirection: 'column', gap: '0.6rem',
              transition: 'all 0.25s',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ ...mono, fontSize: '0.55rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: active ? C.amber : C.dim }}>{a.tag}</span>
                <span style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0, border: `1.5px solid ${active ? C.amber : 'rgba(27,21,48,0.2)'}`, background: active ? C.amber : 'transparent', color: '#fff', fontSize: 11, lineHeight: '15px', textAlign: 'center', transition: 'all 0.25s' }}>
                  {active ? '✓' : ''}
                </span>
              </div>
              <div style={{ ...serif, fontSize: '1.3rem', lineHeight: 1.1, color: C.ink }}>{a.name}</div>
              <div style={{ ...mono, fontSize: '0.68rem', color: C.dim, lineHeight: 1.7 }}>{a.desc}</div>
            </button>
          );
        })}
      </div>

      <div style={{ ...S.card, padding: '1.6rem', display: 'flex', flexDirection: 'column', gap: '1.4rem', maxWidth: '720px' }}>
        <Bar label="Free PLP pool" value={plpPool} color={C.amber} sub="cofactor available to the ~140 dependent enzymes" />
        <Bar label="P6C sequestration rate" value={trap} color={C.storm} sub="relative rate at which P6C is removing PLP from circulation" />
        <p style={{ ...mono, fontSize: '0.78rem', lineHeight: 1.8, color: C.ink, margin: 0 }}>{caption}</p>
      </div>

      <p style={{ ...mono, fontSize: '0.62rem', lineHeight: 1.7, color: C.dim, maxWidth: '720px', marginTop: '1.4rem', fontStyle: 'italic' }}>
        Illustrative model. High-dose pyridoxine, lysine restriction, and arginine supplementation are the established triple-therapy standard of care, and each acts through the distinct mechanism described above. The bar values are schematic — they convey relative direction of effect, not measured changes in free PLP or P6C.
      </p>
    </section>
  );
}

function MobilePathway({ stepIdx, setStepIdx, vis, currentStep }) {
  return (
    <section style={{ padding: '4rem 5vw', borderTop: `1px solid ${C.line}`, background: C.bg }}>
      <div style={S.sectionLabel}>Mechanism of PDE</div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '2rem' }}>
        {STEPS.map((_, i) => (
          <button key={i} onClick={() => setStepIdx(i)} aria-label={`Step ${i + 1}`} style={{
            flex: 1, height: 4, borderRadius: 2, border: 'none', padding: 0,
            background: i === stepIdx ? C.violet : C.line,
            cursor: 'pointer', transition: 'background 0.3s',
          }} />
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
        <PathwaySVG vis={vis} plpLevel={currentStep.plpLevel ?? 1} />
      </div>

      <div style={{ ...mono, fontSize: '0.55rem', letterSpacing: '0.2em', color: C.dim, textTransform: 'uppercase', marginBottom: '0.5rem' }}>
        {currentStep.num}
      </div>
      <div style={{ ...serif, fontSize: 'clamp(1.5rem, 6vw, 2rem)', lineHeight: 1.1, marginBottom: '1rem' }}>
        {currentStep.title}
      </div>
      <div style={{ ...mono, fontSize: '0.78rem', lineHeight: 1.85, color: C.dim, whiteSpace: 'pre-line' }}>
        {currentStep.body}
      </div>
      <span style={badgeStyle(currentStep.tone)}>{currentStep.badge}</span>

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
        <button
          onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
          disabled={stepIdx === 0}
          style={{ flex: 1, padding: '0.8rem', ...mono, fontSize: '0.72rem', border: `1px solid ${C.line}`, borderRadius: 8, background: 'transparent', cursor: stepIdx === 0 ? 'default' : 'pointer', opacity: stepIdx === 0 ? 0.35 : 1, color: C.ink }}
        >
          ← Previous
        </button>
        <button
          onClick={() => setStepIdx(Math.min(STEPS.length - 1, stepIdx + 1))}
          disabled={stepIdx === STEPS.length - 1}
          style={{ flex: 1, padding: '0.8rem', ...mono, fontSize: '0.72rem', border: `1px solid ${C.violet}`, borderRadius: 8, background: 'transparent', color: C.violet, cursor: stepIdx === STEPS.length - 1 ? 'default' : 'pointer', opacity: stepIdx === STEPS.length - 1 ? 0.35 : 1 }}
        >
          Next →
        </button>
      </div>
    </section>
  );
}

function Hero({ refs, isMobile }) {
  return (
    <section style={{
      minHeight: '100svh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      padding: isMobile ? '5rem 1.5rem 3rem' : '6rem 2rem 4rem',
      borderBottom: `1px solid ${C.line}`, position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)',
        width: '70vw', height: '70vw', maxWidth: 900, maxHeight: 900,
        background: 'radial-gradient(circle, rgba(124,58,237,0.10), transparent 65%)',
        pointerEvents: 'none',
      }} />
      <div ref={refs.tag} style={{ ...mono, fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: C.amber, marginBottom: '1.5rem', zIndex: 1 }}>
        OMIM 266100 · ALDH7A1
      </div>
      <h1 ref={refs.h1} style={{ ...serif, fontSize: isMobile ? 'clamp(2.2rem, 10vw, 3.5rem)' : 'clamp(3rem, 8vw, 7rem)', lineHeight: 1.0, letterSpacing: '-0.02em', marginBottom: '1.5rem', zIndex: 1 }}>
        When one enzyme is absent,<br />
        a metabolite accumulates<br />
        and <em style={{ color: C.storm, fontStyle: 'italic' }}>depletes active B6</em>.
      </h1>
      <p ref={refs.p} style={{ maxWidth: '480px', color: C.dim, fontSize: '0.92rem', lineHeight: 1.75, ...mono, zIndex: 1, marginBottom: '2.8rem' }}>
        Pyridoxine-dependent epilepsy (PDE) is caused by variants in <em>ALDH7A1</em>, the gene encoding antiquitin. Without this enzyme, the metabolite P6C accumulates and reacts with pyridoxal 5′-phosphate (PLP) — reducing the active B6 available to ~140 enzymes, including those that produce GABA.
      </p>
      <div ref={refs.facts} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.6rem', zIndex: 1 }}>
        {FACTS.map((f) => (
          <div key={f.k} style={{ ...S.card, padding: '0.7rem 1.1rem', minWidth: 90, display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            <span style={{ ...mono, fontSize: '0.52rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: C.dim }}>{f.k}</span>
            <span style={{ ...mono, fontSize: '0.82rem', color: C.violet }}>{f.v}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function PDEPathway3() {
  const [stepIdx, setStepIdx] = useState(0);
  const [activeMol, setActiveMol] = useState('plp');
  const isMobile = useIsMobile();

  const heroTagRef   = useRef(null);
  const heroH1Ref    = useRef(null);
  const heroPRef     = useRef(null);
  const heroFactsRef = useRef(null);
  const numRef       = useRef(null);
  const titleRef     = useRef(null);
  const bodyRef      = useRef(null);
  const badgeRef     = useRef(null);
  const pathwayRef   = useRef(null);
  const drawerRef    = useRef(null);
  const triggerRef   = useRef(null);

  const currentStep = STEPS[stepIdx];
  const vis = {};
  currentStep.show.forEach((id) => { vis[id] = 1; });
  currentStep.hide.forEach((id) => { vis[id] = 0; });

  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
      const link = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: FONT_URL });
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    drawerRef.current = new SmilesDrawer({ width: 220, height: 130, bondThickness: 0.9,
      themes: { light: { C: '#2a2440', O: '#d97706', N: '#7c3aed', H: '#8b86a0', S: '#ca8a04', P: '#e11d6b', BACKGROUND: 'transparent' } },
    });
    Object.keys(MOLS).forEach((key) => {
      const el = document.getElementById(`pde3-mol-${key}`);
      if (el) {
        try { drawerRef.current.draw(MOLS[key].smiles, `#pde3-mol-${key}`, 'light', false); } catch (_) {}
      }
    });
  }, []);

  useEffect(() => {
    const els = [heroTagRef.current, heroH1Ref.current, heroPRef.current, heroFactsRef.current];
    gsap.set(els, { y: 24, opacity: 0 });
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
    tl.to(heroTagRef.current,   { opacity: 1, y: 0, duration: 0.7, delay: 0.2 })
      .to(heroH1Ref.current,    { opacity: 1, y: 0, duration: 0.9 }, '-=0.3')
      .to(heroPRef.current,     { opacity: 1, y: 0, duration: 0.7 }, '-=0.5')
      .to(heroFactsRef.current, { opacity: 1, y: 0, duration: 0.6 }, '-=0.3');
    return () => tl.kill();
  }, []);

  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    const targets = [numRef.current, titleRef.current, bodyRef.current, badgeRef.current].filter(Boolean);
    const tl = gsap.timeline();
    tl.to(targets, { opacity: 0, y: -10, duration: 0.18, stagger: 0.04 })
      .to(targets, { opacity: 1, y: 0,   duration: 0.32, stagger: 0.06 });
    return () => tl.kill();
  }, [stepIdx]);

  useEffect(() => {
    if (isMobile || !pathwayRef.current) return;
    const SCROLL_PER_STEP = 2.6;
    triggerRef.current = ScrollTrigger.create({
      trigger: pathwayRef.current,
      start: 'top top',
      end: `+=${window.innerHeight * (STEPS.length - 1) * SCROLL_PER_STEP}`,
      pin: true,
      scrub: 1.2,
      snap: { snapTo: 1 / (STEPS.length - 1), duration: { min: 0.35, max: 0.8 }, delay: 0.2, ease: 'power2.inOut' },
      onUpdate: (self) => {
        const idx = Math.round(self.progress * (STEPS.length - 1));
        setStepIdx((prev) => (prev !== idx ? idx : prev));
      },
    });
    return () => { triggerRef.current?.kill(); triggerRef.current = null; };
  }, [isMobile]);

  const spad = sec(isMobile);

  return (
    <div style={S.root}>
      <Hero refs={{ tag: heroTagRef, h1: heroH1Ref, p: heroPRef, facts: heroFactsRef }} isMobile={isMobile} />

      {/* Neonatal timeline */}
      <section style={{ ...spad, borderTop: `1px solid ${C.line}`, background: C.bgAlt }}>
        <div style={S.sectionLabel}>Clinical presentation</div>
        <div style={{ ...S.sectionHeading, maxWidth: '620px', marginBottom: '1.2rem' }}>
          From birth to diagnosis.
        </div>
        <p style={{ ...S.sectionBlurb, maxWidth: '560px', marginBottom: '3rem' }}>
          PDE typically presents without prenatal warning. The combination of early-onset neonatal seizures, failure to respond to standard anticonvulsants, and a clear response to pyridoxine is what guides clinicians toward the diagnosis.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.8rem' }}>
          {TIMELINE.map((m, i) => {
            const isTest = i === 3 || i === 4;
            return (
              <div key={m.label} style={{
                ...S.card,
                borderColor: isTest ? 'rgba(13,148,136,0.4)' : C.line,
                background: isTest ? 'rgba(13,148,136,0.05)' : C.bg,
                padding: '1.2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
              }}>
                <span style={{ ...mono, fontSize: '0.52rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: isTest ? C.calm : C.amber }}>
                  {String(i + 1).padStart(2, '0')} · {m.t}
                </span>
                <span style={{ ...serif, fontSize: '1.2rem', lineHeight: 1.15 }}>{m.label}</span>
                <span style={{ ...mono, fontSize: '0.67rem', color: C.dim, lineHeight: 1.7 }}>{m.note}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pathway — both always in DOM; CSS toggles which is visible.
          Conditional rendering would let React try to removeChild a node that
          ScrollTrigger has moved into its pin-spacer, causing a DOM mismatch crash.
          Visibility wrapper is separate from pinned element so GSAP can't override display. */}
      <div style={{ display: isMobile ? 'none' : 'block' }}>
      <section ref={pathwayRef} style={{ position: 'relative' }}>
        <div style={{
          height: '100vh', display: 'grid',
          gridTemplateColumns: '130px 1fr 360px',
          alignItems: 'center', padding: '0 5vw', gap: '2.5rem', overflow: 'hidden',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem', ...mono, fontSize: '0.6rem', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            {STEPS.map((s, i) => {
              const active = i === stepIdx;
              return (
                <div key={i} style={{ color: active ? C.violet : C.dim, paddingLeft: '1rem', borderLeft: active ? `2px solid ${C.violet}` : `2px solid ${C.line}`, transition: 'all 0.4s', opacity: active ? 1 : 0.5 }}>
                  {s.chapter}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <PathwaySVG vis={vis} plpLevel={currentStep.plpLevel ?? 1} />
          </div>
          <div>
            <div ref={numRef} style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.2em', color: C.dim, textTransform: 'uppercase', marginBottom: '0.6rem' }}>{currentStep.num}</div>
            <div ref={titleRef} style={{ ...serif, fontSize: 'clamp(1.6rem, 2.5vw, 2.4rem)', lineHeight: 1.08, marginBottom: '1.2rem' }}>{currentStep.title}</div>
            <div ref={bodyRef} style={{ ...mono, fontSize: '0.78rem', lineHeight: 1.85, color: C.dim, whiteSpace: 'pre-line' }}>{currentStep.body}</div>
            <span ref={badgeRef} style={badgeStyle(currentStep.tone)}>{currentStep.badge}</span>
          </div>
        </div>
      </section>
      </div>

      <div style={{ display: isMobile ? 'block' : 'none' }}>
        <MobilePathway stepIdx={stepIdx} setStepIdx={setStepIdx} vis={vis} currentStep={currentStep} />
      </div>

      {/* Catabolic cascade */}
      <section style={{ ...spad, paddingBottom: isMobile ? '3rem' : '7rem', borderTop: `1px solid ${C.line}`, background: C.bg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '2rem', maxWidth: '1200px', marginBottom: '2.5rem' }}>
          <div>
            <div style={S.sectionLabel}>Lysine catabolism</div>
            <div style={{ ...S.sectionHeading, maxWidth: '620px' }}>One blocked step. Two accumulating metabolites.</div>
          </div>
          <p style={{ ...S.sectionBlurb, maxWidth: '380px' }}>
            Lysine is degraded via the saccharopine pathway in the mitochondria. Antiquitin catalyzes the conversion of α-AASA to α-aminoadipate. When this step is blocked, α-AASA accumulates. Because α-AASA and P6C are in spontaneous equilibrium, the P6C pool — which reacts with PLP — is continuously replenished.
          </p>
        </div>
        <div style={{ width: '100%', overflowX: 'auto', paddingBottom: '1rem' }}>
          <CascadeSVG />
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '1.5rem', ...mono, fontSize: '0.65rem', letterSpacing: '0.12em', color: C.dim, textTransform: 'uppercase' }}>
          {[['active step', C.violet], ['blocked / accumulating', C.storm], ['cofactor sequestration', C.amber], ['minor route', 'rgba(106,100,126,0.6)']].map(([t, col]) => (
            <span key={t}><span style={{ display: 'inline-block', width: 10, height: 10, background: col, marginRight: 7, verticalAlign: 'middle' }} />{t}</span>
          ))}
        </div>
      </section>

      <DependencyWeb isMobile={isMobile} />
      <TripleTherapy isMobile={isMobile} />

      {/* Molecule viewer */}
      <section style={{ ...spad, borderTop: `1px solid ${C.line}`, background: C.bg }}>
        <div style={S.sectionLabel}>Key molecules</div>
        <div style={{ ...S.sectionHeading, marginBottom: '2.5rem' }}>Six molecules, one chain of events.</div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem', maxWidth: '1100px' }}>
          {Object.keys(MOLS).map((key) => {
            const m = MOLS[key];
            const active = activeMol === key;
            const roleColor = m.role === 'Therapy' ? C.amber : (m.role === 'PLP trap' || m.role === 'Accumulates') ? C.storm : C.violet;
            return (
              <div key={key} onClick={() => setActiveMol(key)} style={{
                ...S.card,
                borderColor: active ? C.violet : C.line,
                background: active ? 'rgba(124,58,237,0.06)' : C.bg,
                padding: '1.2rem', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: '0.6rem',
                minHeight: '260px', transition: 'all 0.3s',
              }}>
                <div style={{ ...mono, fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: roleColor }}>{m.role}</div>
                <div style={{ ...serif, fontSize: '1.4rem' }}>{m.name}</div>
                <div style={{ background: 'rgba(27,21,48,0.03)', border: `1px solid rgba(27,21,48,0.07)`, minHeight: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem', borderRadius: 6 }}>
                  <canvas id={`pde3-mol-${key}`} width={220} height={130} style={{ display: 'block', maxWidth: '100%' }} />
                </div>
                <div style={{ ...mono, fontSize: '0.68rem', color: C.dim, lineHeight: 1.7 }}>{m.desc}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* How to help */}
      <section style={{ ...spad, paddingBottom: isMobile ? '3rem' : '4rem', borderTop: `1px solid ${C.violetLo}`, background: C.bgAlt }}>
        <div style={S.sectionLabel}>Get Involved</div>
        <div style={{ ...S.sectionHeading, marginBottom: '1.4rem' }}>How You Can Help</div>
        <p style={{ ...S.sectionBlurb, maxWidth: '640px', marginBottom: '3rem' }}>
          PDE affects very few people, which means natural-history data and gene-therapy trials only become possible when families find each other and support the research. Patient registries and community networks are essential infrastructure for rare disease science.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.6rem', maxWidth: '860px', marginBottom: '3rem' }}>
          {HELP_WAYS.map((w) => (
            <div key={w.t} style={{ borderTop: `2px solid ${C.violet}`, paddingTop: '1rem' }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1rem', fontWeight: 600, color: C.ink, marginBottom: '0.5rem' }}>{w.t}</div>
              <div style={{ ...mono, fontSize: '0.72rem', color: C.dim, lineHeight: 1.8 }}>{w.d}</div>
            </div>
          ))}
        </div>
        <div style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.25em', color: C.dim, textTransform: 'uppercase', marginBottom: '1rem' }}>
          Foundations Worth Supporting
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: '860px' }}>
          {HELP_FOUNDATIONS.map((f) => (
            <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
               style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem', padding: '1.2rem 1.4rem', background: C.bg, border: `1px solid ${C.line}`, borderRadius: '10px', textDecoration: 'none', transition: 'border-color 0.18s, background 0.18s' }}
               onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.violetLo; e.currentTarget.style.background = 'rgba(124,58,237,0.05)'; }}
               onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.line; e.currentTarget.style.background = C.bg; }}>
              <div>
                <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.9rem', fontWeight: 600, color: C.ink, marginBottom: '0.25rem' }}>{f.name}</div>
                <div style={{ ...mono, fontSize: '0.7rem', color: C.dim, lineHeight: 1.65 }}>{f.desc}</div>
              </div>
              <span style={{ ...mono, fontSize: '1rem', color: C.violet, flexShrink: 0 }}>&rarr;</span>
            </a>
          ))}
        </div>
      </section>

      {/* Sources */}
      <section style={{ ...spad, paddingBottom: isMobile ? '3rem' : '4rem', borderTop: `1px solid ${C.line}`, background: C.bgAlt }}>
        <div style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.25em', color: C.violet, textTransform: 'uppercase', marginBottom: '0.8rem' }}>References</div>
        <div style={{ ...S.sectionHeading, marginBottom: '2rem' }}>Sources</div>
        {SOURCES.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: '1rem', padding: '1rem 0', borderTop: `1px solid ${C.line}`, maxWidth: '860px' }}>
            <span style={{ ...mono, fontSize: '0.7rem', color: C.violet, flexShrink: 0, width: '1.5rem' }}>{String(i + 1).padStart(2, '0')}</span>
            <a href={s.href} target="_blank" rel="noopener noreferrer" style={{ ...mono, fontSize: '0.73rem', lineHeight: 1.75, color: C.dim, textDecoration: 'none' }}>{s.cite}</a>
          </div>
        ))}
      </section>

      <footer style={{ padding: isMobile ? '2rem 5vw 4rem' : '3rem 6vw 5rem', ...mono, fontSize: '0.6rem', letterSpacing: '0.2em', color: C.dim, textTransform: 'uppercase' }}>
        Educational illustration · not for clinical use · last reviewed 2026
      </footer>

      <ContactFooter />

      <style>{`
        .pde3-range {
          -webkit-appearance: none; appearance: none;
          height: 6px; border-radius: 999px;
          background: linear-gradient(90deg, ${C.violet}, rgba(124,58,237,0.15));
          outline: none; width: 100%;
        }
        .pde3-range::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 24px; height: 24px; border-radius: 999px;
          background: ${C.violet}; border: 3px solid #fff;
          box-shadow: 0 1px 6px rgba(124,58,237,0.5); cursor: pointer;
        }
        .pde3-range::-moz-range-thumb {
          width: 24px; height: 24px; border-radius: 999px;
          background: ${C.violet}; border: 3px solid #fff;
          box-shadow: 0 1px 6px rgba(124,58,237,0.5); cursor: pointer; border: none;
        }
      `}</style>
    </div>
  );
}
